import React from 'react';
import { Link } from 'react-router-dom';

import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Quote } from '../../../../models/quote.model';
import { Order } from '../../../../models/order.model';
import { Invoice } from '../../../../models/invoice.model';
import { dateFormat, datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { countryName } from '../../../../libraries/country.library';
import { formatCantonDisplay } from '../../../../data/cantons';
import { languageValue } from '../../../../libraries/language.library';
import { qrTypeValue } from '../../../../libraries/qr.type.library';
import { contactTypeValue } from '../../../../libraries/contact.type.library';
import { formatAddress, formatContact, formatCurrency, formatNumberAddress, normalizeContactData } from '../../../../scripts/format.value.script';

export interface AppSaleViewDetailProps {
  sale: string,
  record: any | Quote | Order | Invoice
};

const AppSaleViewDetail: React.FunctionComponent<AppSaleViewDetailProps> = ({sale, record}) => {
  const {lang} = StateLanguage()

  if (record?.customer) normalizeContactData(record.customer)

  return (
    <div className="card-body">
      <div className="d-flex flex-wrap">
        <div className="flex-equal me-5">
          <table className="table align-middle gy-1 m-0">
            <tbody className="fw-bold">
              { sale !== "quote" &&
                <tr>
                  <td className="w-200px text-muted">{record.id_quotes && record.id_quotes.filter((q: any) => typeof q === 'object' && q.number).length > 1 ? lang.labels.quotes : lang.labels.quoteNumber}:</td>
                  <td className="min-w-200px">
                    { record.id_quotes && record.id_quotes.filter((q: any) => typeof q === 'object' && q.number).length > 0
                      ? record.id_quotes
                          .filter((q: any) => typeof q === 'object' && q.number)
                          .map((quote: any, i: number, arr: any[]) => (
                            <span key={quote.id}>
                              <Link to={`/main/sale/quote/view/${quote.id}`} className="text-primary">{quote.number}</Link>
                              {i < arr.length - 1 && ', '}
                            </span>
                          ))
                      : record.quote?.quote_number
                        ? record.quote.quote_number
                        : lang.labels.none
                    }
                  </td>
                </tr>
              }
              { sale === "invoice" &&
                <tr>
                  <td className="w-200px text-muted">{record.id_orders && record.id_orders.filter((o: any) => typeof o === 'object' && o.number).length > 1 ? lang.labels.orders : lang.labels.orderNumber}:</td>
                  <td className="min-w-200px">
                    { record.id_orders && record.id_orders.filter((o: any) => typeof o === 'object' && o.number).length > 0
                      ? record.id_orders
                          .filter((o: any) => typeof o === 'object' && o.number)
                          .map((order: any, i: number, arr: any[]) => (
                            <span key={order.id}>
                              <Link to={`/main/sale/order/view/${order.id}`} className="text-primary">{order.number}</Link>
                              {i < arr.length - 1 && ', '}
                            </span>
                          ))
                      : record.order?.order_number
                        ? record.order.order_number
                        : lang.labels.none
                    }
                  </td>
                </tr>
              }
              <tr>
                <td className="w-200px text-muted">{lang.labels[`${sale}Reference` as keyof typeof lang.labels]}:</td>
                <td className="min-w-200px">{(record.reference ?? '').length > 0 ? record.reference : lang.labels.none}</td>
              </tr>
              { sale === 'invoice' && record.source?.service === 'digital-commerce-recurring' && record.source?.external_ref &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.recurringContract}:</td>
                  <td className="min-w-200px">
                    <Link to={`/main/sale/recurring-contract/view/${record.source.external_ref}`} className="text-primary">{record.source.external_number ?? record.source.external_ref}</Link>
                  </td>
                </tr>
              }
              <tr>
                <td className="w-200px text-muted">{lang.labels.concerning}:</td>
                <td className="min-w-200px">{record.concerning}</td>
              </tr>
              <tr>
                <td className="w-200px text-muted">{lang.labels.company}:</td>
                <td className="min-w-200px">{record.company?.name ?? ''}</td>
              </tr>
              <tr>
                <td valign="top" className="w-200px text-muted">{lang.labels.customer}:</td>
                <td className="min-w-200px">
                  <div className="d-flex align-items-center">
                    {formatContact(record.customer)}
                    <span className="badge badge-sm badge-light text-uppercase fs-9 ms-3">{contactTypeValue(lang, record.customer?.type ?? '')}</span>
                  </div>
                  {(record.customer?.data?.contact_code ?? '').length > 0 &&
                    <p className="text-secondary fs-8 p-0 m-0">{lang.labels.code}: {record.customer.data.contact_code}</p>
                  }
                  <p className="text-secondary fs-8 p-0 m-0">{record.customer?.data?.address?.street ?? ''} {formatNumberAddress(lang, record.customer?.data?.address?.number ?? '')}</p>
                  <p className="text-secondary fs-8 p-0 m-0">{record.customer?.data?.address?.city ?? ''}{(record.customer?.data?.address?.zip_code ?? '').length > 0 && ` - ${record.customer.data.address.zip_code}`}</p>
                  {(record.customer?.data?.address?.administrative_area ?? '').length > 0 &&
                    <p className="text-secondary fs-8 p-0 m-0">{formatCantonDisplay(record.customer.data.address.administrative_area, lang.code)}</p>
                  }
                  <p className="text-secondary fs-8 p-0 m-0">{countryName(lang.code, record.customer?.data?.address?.country ?? '')}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex-equal">
          <table className="table align-middle gy-1 m-0">
            <tbody className="fw-bold">
              <tr>
                <td className="w-200px text-muted">{lang.labels[`${sale}Date` as keyof typeof lang.labels]}:</td>
                <td className="min-w-200px">{dateFormat(lang.code, record.created_date)}</td>
              </tr>
              <tr>
                <td className="w-200px text-muted">{lang.labels.validUntil}:</td>
                <td className="min-w-200px">{datetimeFormat12h(lang.code, record.valid_until)}</td>
              </tr>
              <tr>
                <td className="w-200px text-muted">{lang.labels.createdAt}:</td>
                <td className="min-w-200px">{datetimeFormat12h(lang.code, record.created_at)}</td>
              </tr>
              { (sale === "order" || sale === "invoice") && record.delivery_address &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.deliveyAddress}:</td>
                  <td className="min-w-200px">{formatAddress(lang, record.delivery_address?.address ?? record.delivery_address)}</td>
                </tr>
              }
              { record.delivery_date &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.deliveryDate}:</td>
                  <td className="min-w-200px">{dateFormat(lang.code, record.delivery_date)}</td>
                </tr>
              }
              <tr>
                <td className="w-200px text-muted">{lang.labels.currency}:</td>
                <td className="min-w-200px">{formatCurrency(record.currency)}</td>
              </tr>
              { sale === 'invoice' &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.treasuryAccount}:</td>
                  <td className="min-w-200px">{record.treasury_account?.name ?? ''}</td>
                </tr>
              }
              <tr>
                <td className="w-200px text-muted">{lang.labels.language}:</td>
                <td className="min-w-200px">{languageValue(lang, record.language)}</td>
              </tr>
              { sale === "invoice" &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.qrType}:</td>
                  <td className="min-w-200px">{qrTypeValue(lang, record.qr_type)}</td>
                </tr>
              }
              { sale === 'invoice' &&
                <tr>
                  <td className="w-200px text-muted">{lang.labels.dunningPolicy}:</td>
                  <td className="min-w-200px">
                    { record.dunning?.policy_snapshot?.label
                      ? record.dunning.policy_snapshot.label
                      : (record.dunning_policy_id ? lang.labels.dunningConfigured : lang.labels.noDunning) }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
};

export default AppSaleViewDetail;
